import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux';

import _ from 'lodash';
import cx from 'classnames';
import LinesEllipsis from 'react-lines-ellipsis';
import { IKImage } from "imagekitio-react";
import { Row, Col, Button } from 'react-bootstrap';
import dateformat from "dateformat";

import Tooltip from 'components/Tooltip';
import FileService from 'services/file.service';

import styles from './components.module.scss';

const defaultContentImage = 'https://app.paperflite.com/public/9ef538f61f7758b31344ad75a05b0775.jpg';

const stripHtmlFromText = (value) => {
  if (!value || typeof value !== 'string') {
    return '';
  }

  const doc = new DOMParser().parseFromString(value, 'text/html');
  return doc.body.textContent || '';
};

export const AssetCard = ({ assetMapping, key, id, selected, onClick, handleDelete, container }) => {

  const icon = _.get(assetMapping, 'asset.icon.thumbnail', defaultContentImage);
  const [tooltip, setTooltip] = useState(false)
  const tooltipContainer = container?.current || document.body;
  const metaData = _.get(assetMapping, 'asset.metadata');
  const uploading = _.get(assetMapping, 'uploading');
  const creating = _.get(assetMapping, 'creating') || _.get(assetMapping, 'creatingAssetMapping');
  const optimizing =  _.get(assetMapping, 'asset.entityType') === "asset" && _.get(assetMapping, 'asset.status') != "OK";
  const deleting = _.get(assetMapping, 'deleting');

  const progress = uploading || creating || optimizing;

  const handleAssetSection = (e) => {
    const id = _.get(assetMapping, 'id', null);
    const type = _.get(assetMapping, 'entityType', null);
    if (!id || type != 'asset' || deleting) {
      return;
    }

    onClick(e, assetMapping);
  }

  const renderAssetName = () => {
    const type = _.get(assetMapping, 'entityType');

    switch (type) {
      case 'asset':
        return stripHtmlFromText(_.get(assetMapping, 'asset.name', ''));
      case 'url':
        return _.get(assetMapping, 'url', '');
      case 'file':
        return _.get(assetMapping, 'file.name', '')
      default:
        return "";
    }
  }

  const handleTooltip = (truncated) => {
    const { clamped } = truncated;
    setTooltip(clamped);
  }

  return (
    <div key={key} id={id} onClick={handleAssetSection} className={cx(styles.assetCardWrapper, 'cs-asset-card', { ['cs-selected-asset-card']: selected }, {
      [styles.selected]: selected
    })}>
      <Button onClick={() => handleDelete(_.get(assetMapping, 'asset.id'))} className={styles.deleteAssetButton} variant='info'>
        <i className={cx(styles.closeIcon, 'cleverstory-close')} />
      </Button>
      <div className={styles.iconWrapper}>
        <img className={styles.icon} src={icon} alt="" />
      </div>
      <div className={styles.detailsContainer}>
        <Tooltip
          className={styles.tooltip}
          placement="top"
          disabled={!tooltip}
          content={renderAssetName()}
          container={tooltipContainer}
        >
          <span className={styles.name}>
            <LinesEllipsis basedOn="letters" onReflow={handleTooltip} text={renderAssetName()} maxLine='1' ellipsis='...' />
          </span>
        </Tooltip>
        <ContentMetadata content={assetMapping} />
      </div>
    </div>
  )
}

export const ContentMetadata = ({ content, className }) => {
  const metaData = _.get(content, 'asset.metadata', {})
  let fileMetaData = FileService.fileMetaData(metaData);

  return (
    <div className={styles.metaData}>
      <div className={styles.details}>
        <div className={styles.type}>
          <i className={cx(styles.fileIcon, `${FileService.fileIcon(metaData)}`)} />
          <span className={styles.textFormat} >{FileService.fileType(metaData)}</span>
        </div>
        {fileMetaData && <i className={styles.seperator}></i>}
        {fileMetaData && (
          <span className={styles.contentMetadata} title={fileMetaData}>
            {fileMetaData}
          </span>
        )}
      </div>
      <span className={styles.date}>{dateformat(metaData.modified_date, 'dd mmm yyyy')}</span>
    </div>
  );
}

export const ContentCardPlaceholder = () => {
  return (
    <div className={cx(styles.contentCard, styles.placeholder)}>
      <div className={cx(styles.iconWrapper)}></div>
      <div className={styles.detailsContainer}>
        <div className={styles.name}>
          <span>&nbsp;</span>
          <span>&nbsp;</span>
        </div>
        <div className={cx(styles.contentMetadata)}>
          {_.times(2).map(index => {
            return (
              <div key={index} className={styles.metadata}><span>&nbsp;</span></div>
            );
          })}
        </div>
      </div>
    </div>
  )
}

export const StreamCard = ({ index, onSelect, stream, container }) => {
  const [tooltip, setTooltip] = useState(false)

  const icon = _.get(stream, 'icon.thumbnail', 'https://app.paperflite.com/public/9ef538f61f7758b31344ad75a05b0775.jpg');
  const streamName = stripHtmlFromText(_.get(stream, 'name', ''));
  const tooltipContainer = container?.current || document.body;

  const handleTooltip = (truncated) => {
    const { clamped } = truncated;
    setTooltip(clamped);
  }

  return (
    <div key={index} onClick={() => { onSelect(stream) }} className={styles.streamsCardWrapper}>
      <img className={styles.icon} src={icon} alt="" />
      <div className={styles.overlay}>
        <Tooltip
          className={styles.tooltip}
          placement="top"
          disabled={!tooltip}
          content={streamName}
          container={tooltipContainer}
        >
          <span className={styles.title}>
            <LinesEllipsis
              basedOn="letters"
              onReflow={handleTooltip}
              text={streamName}
              maxLine="2"
              ellipsis="..."
            />
          </span>
        </Tooltip>
      </div>
    </div>
  )
}

export const AssetCardRender = ({ key, asset, onSelect, container }) => {
  const [tooltip, setTooltip] = useState(false)

  const icon = _.get(asset, 'icon.thumbnail', 'https://app.paperflite.com/public/9ef538f61f7758b31344ad75a05b0775.jpg');
  const metaData = _.get(asset, 'metadata', {});
  const assetName = stripHtmlFromText(_.get(asset, 'name', ''));
  const assetSize = _.get(metaData, 'content_length');
  const fileType = FileService.fileType(metaData);
  const durationOrMeta = FileService.fileMetaData(metaData);
  const tooltipContainer = container?.current || document.body;

  const renderGridMetadata = () => {
    if (fileType === 'LINK') {
      return null;
    }

    const hasSize = Boolean(assetSize);
    const hasPages = Boolean(_.get(metaData, 'pages'));

    const metaParts = [];
    if (hasSize) {
      metaParts.push(FileService.formatSize(assetSize));
    }
    if (hasPages) {
      metaParts.push(`${_.get(metaData, 'pages')} Pages`);
    }
    if (!metaParts.length) {
      if (!durationOrMeta) {
        return null;
      }
      metaParts.push(durationOrMeta);
    }

    const metaLabel = metaParts.join(' | ');

    return (
      <div className={styles.gridCardMeta}>
        <span className={styles.metaSummary} title={metaLabel}>
          {metaLabel}
        </span>
      </div>
    );
  };

  const uploadedAssets = useSelector((state) =>
    (state.content.uploads || []).filter(
      (asset) => _.get(asset, 'asset.source') === 'paperflite'
    )
  );

  const handleTooltip = (truncated) => {
    const { clamped } = truncated;
    setTooltip(clamped);
  }

  return (
    <div key={key} onClick={() => { onSelect(asset) }} className={cx(styles.assetContentWrapper)}>
      <div className={cx(styles.imageContainer, { [styles.selected]: _.some(uploadedAssets, (data) => _.get(data, 'asset.id') === asset.id) })}>
        <img className={styles.thumbnail} src={icon} alt="" />
        <div className={styles.overlay}>
          <span className={styles.assetType}>
            {fileType}
          </span>
        </div>
      </div>
      <Tooltip
        className={styles.tooltip}
        placement="top"
        disabled={!tooltip}
        content={assetName}
        container={tooltipContainer}
      >
        <span className={styles.assetName}>
          <LinesEllipsis
            basedOn="letters"
            onReflow={handleTooltip}
            text={assetName}
            maxLine='2'
            ellipsis='...'
          />
        </span>
      </Tooltip>
      {renderGridMetadata()}
    </div>
  )
}

export const GroupCardRender = ({ key, onSelect, group }) => {
  const [tooltip, setTooltip] = useState(false)

  const icon = _.get(group, 'icon.thumbnail', 'https://app.paperflite.com/public/9ef538f61f7758b31344ad75a05b0775.jpg');

  const handleTooltip = (truncated) => {
    const { clamped } = truncated;
    setTooltip(clamped);
  }

  return (
    <div key={key} onClick={() => { onSelect(group) }} className={styles.groupCardWrapper}>
      <div className={styles.imageContainer}>
        <div className={cx(styles.layer, styles.layer1)}></div>
        <div className={cx(styles.layer, styles.layer2)}></div>
        <img className={styles.thumbnail} src={icon} alt="" />
      </div>
      <Tooltip className={styles.tooltip} placement="top" disabled={!tooltip} content={group.name}> 
      <span className={styles.assetName}>
        <LinesEllipsis
          basedOn="letters"
          text={group.name}
          onReflow={handleTooltip}
          maxLine='2'
          ellipsis='...'
        />
      </span>
      </Tooltip>
    </div>
  )
}